import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './Listings.css';

function Listings({ user }) {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [propertyType, setPropertyType] = useState('all');

  useEffect(() => {
    fetchListings();
  }, []);

  useEffect(() => {
    filterListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings, searchTerm, propertyType]);

  const fetchListings = async () => {
    try {
      const response = await axios.get('/api/listings');
      const activeListings = (response.data.listings || []).filter(l => l.status === 'active');
      setListings(activeListings);
      setFilteredListings(activeListings);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterListings = () => {
    let filtered = [...listings];

    // Filter by property type category
    if (propertyType !== 'all') {
      filtered = filtered.filter(listing => {
        const type = listing.property_type?.toLowerCase();
        if (propertyType === 'residential') {
          return ['single_family', 'multi_family', 'condo', 'townhouse'].includes(type);
        } else if (propertyType === 'land') {
          return type === 'land';
        } else if (propertyType === 'commercial') {
          return type === 'commercial';
        }
        return true;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(listing =>
        listing.title?.toLowerCase().includes(term) ||
        listing.address?.toLowerCase().includes(term) ||
        listing.city?.toLowerCase().includes(term) ||
        listing.zip_code?.toLowerCase().includes(term) ||
        listing.description?.toLowerCase().includes(term)
      );
    }

    setFilteredListings(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    filterListings();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePropertyTypeChange = (type) => {
    setPropertyType(type);
  };

  if (loading) {
    return <div className="loading"></div>;
  }

  return (
    <div className="listings-page">
      <div className="listings-container">
        {/* Header */}
        <div className="listings-header">
          <h1>Browse Properties</h1>
          <p>Find your perfect home</p>
        </div>

        {/* Search Section */}
        <div className="search-section">
          <div className="search-bar">
            <svg 
              className="search-icon" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by city, address, or ZIP code..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
            />
            <button className="search-btn" onClick={handleSearch}>
              Search
            </button>
          </div>

          {/* Property Type Filter */}
          <div className="filter-group">
            <label className="filter-label">Property Type</label>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${propertyType === 'all' ? 'active' : ''}`}
                onClick={() => handlePropertyTypeChange('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${propertyType === 'residential' ? 'active' : ''}`}
                onClick={() => handlePropertyTypeChange('residential')}
              >
                Residential
              </button>
              <button
                className={`filter-btn ${propertyType === 'land' ? 'active' : ''}`}
                onClick={() => handlePropertyTypeChange('land')}
              >
                Land
              </button>
              <button
                className={`filter-btn ${propertyType === 'commercial' ? 'active' : ''}`}
                onClick={() => handlePropertyTypeChange('commercial')}
              >
                Commercial
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <p className="results-count">
          {filteredListings.length} {filteredListings.length === 1 ? 'property' : 'properties'} found
        </p>

        {/* Listings Grid */}
        <div className="listings-grid">
          {filteredListings.map(listing => (
            <Link 
              key={listing.id} 
              to={`/listings/${listing.id}`} 
              className="property-card"
            >
              {/* Property Image */}
              <div className="property-image">
                {listing.images && listing.images.length > 0 ? (
                  <img src={listing.images[0].image_url} alt={listing.title} />
                ) : (
                  <div className="property-placeholder">No Image Available</div>
                )}
                {listing.status && (
                  <span className={`status-badge status-${listing.status.toLowerCase()}`}>
                    {listing.status}
                  </span>
                )}
              </div>

              {/* Property Content */}
              <div className="property-content">
                <p className="property-price">
                  ${parseInt(listing.price).toLocaleString()}
                </p>
                <p className="property-address">
                  {listing.address}
                  {listing.city && `, ${listing.city}`}
                  {listing.state && `, ${listing.state}`}
                </p>
                <div className="property-specs">
                  {listing.bedrooms && (
                    <span>{listing.bedrooms} bed</span>
                  )}
                  {listing.bathrooms && (
                    <span>{listing.bathrooms} bath</span>
                  )}
                  {listing.square_feet && (
                    <span>{parseInt(listing.square_feet).toLocaleString()} sqft</span>
                  )}
                  {listing.acreage && (
                    <span>{listing.acreage} acres</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Listings;
